import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { AlertTriangle, X } from "lucide-react";

const REASONS = [
  { id: "too_expensive", label: "Too expensive" },
  { id: "found_job", label: "Found a job" },
  { id: "bug_or_frustration", label: "Bug or frustration" },
  { id: "other", label: "Other" },
];

export default function DeleteAccountModal({ open, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [reasonDetail, setReasonDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const canSubmit = password.length > 0 && confirmText === "DELETE" && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/account/delete", {
        password,
        confirm: confirmText,
        reason: reason || null,
        reason_detail: reasonDetail.trim().slice(0, 500) || null,
      });
      onDeleted?.();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto"
      style={{ background: "rgba(15,27,45,0.55)" }}
      data-testid="delete-account-modal"
    >
      <div
        className="rm-card-static bg-white w-full max-w-md p-6 md:p-7 relative my-auto"
        style={{ border: "4px solid #DC2626" }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100"
          aria-label="Close"
          data-testid="delete-account-cancel-x"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={22} style={{ color: "#DC2626" }} strokeWidth={2.5} />
          <h2 className="font-display text-xl uppercase" style={{ color: "#DC2626" }}>
            Delete your account
          </h2>
        </div>

        <p className="text-sm mb-4" style={{ color: "#475569" }}>
          This permanently erases your resume, every tailored analysis, skill assessments,
          role research, interview banks, mock interview transcripts, stepping stones,
          feedback, credits, and the account itself.{" "}
          <strong style={{ color: "#0F1B2D" }}>There is no recovery.</strong>
        </p>

        <form onSubmit={submit} className="space-y-3">
          {/* Optional churn survey */}
          <div data-testid="churn-survey">
            <span className="rm-label block mb-1">
              Mind sharing why you're leaving? <span style={{ color: "#94A3B8", fontWeight: 400 }}>(optional)</span>
            </span>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => {
                const active = reason === r.id;
                return (
                  <button
                    type="button"
                    key={r.id}
                    onClick={() => setReason(active ? "" : r.id)}
                    disabled={busy}
                    className="rm-btn text-xs"
                    style={{
                      background: active ? "#0F1B2D" : "white",
                      color: active ? "white" : "#0F1B2D",
                      border: "3px solid #0F1B2D",
                      padding: "8px 10px",
                      justifyContent: "center",
                    }}
                    data-testid={`churn-reason-${r.id}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              disabled={busy}
              placeholder="Anything else? (optional, max 500 chars)"
              maxLength={500}
              rows={2}
              className="w-full mt-2 px-3 py-2 rounded font-mono-rm text-xs"
              style={{ border: "3px solid #0F1B2D", background: "white", resize: "vertical" }}
              data-testid="churn-detail"
            />
          </div>

          <div className="pt-3" style={{ borderTop: "2px dashed #DC2626" }}>
            <label className="block">
              <span className="rm-label block mb-1">Confirm your password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 rounded font-mono-rm text-sm"
                style={{ border: "3px solid #0F1B2D", background: "white" }}
                data-testid="delete-account-password"
                disabled={busy}
              />
            </label>
          </div>

          <label className="block">
            <span className="rm-label block mb-1">
              Type <strong style={{ color: "#DC2626" }}>DELETE</strong> to confirm
            </span>
            <input
              type="text"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              required
              className="w-full px-3 py-2 rounded font-mono-rm text-sm uppercase tracking-widest"
              style={{ border: "3px solid #0F1B2D", background: "white" }}
              data-testid="delete-account-confirm-text"
              disabled={busy}
              placeholder="DELETE"
            />
          </label>

          {error && (
            <div
              className="p-2 rounded font-mono-rm text-xs"
              style={{ background: "#FEF2F2", border: "2px solid #EF4444", color: "#991B1B" }}
              data-testid="delete-account-error"
            >
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rm-btn rm-btn-ghost text-xs flex-1"
              data-testid="delete-account-cancel-btn"
            >
              Keep my account
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rm-btn text-xs flex-1"
              style={{
                background: canSubmit ? "#DC2626" : "#FCA5A5",
                color: "white",
                border: "3px solid #0F1B2D",
              }}
              data-testid="delete-account-confirm-btn"
            >
              {busy ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
